/**
 * Validate expandable inputs (inputs inside .expandable-group)
 * When a radio/checkbox in expandable-group is checked, validate its child inputs
 * @param {HTMLElement} step - Step element
 * @returns {boolean} - Whether all expandable inputs are valid
 */
function validateExpandableInputs(step) {
	const expandableGroups = step.querySelectorAll('.expandable-group');
	let isValid = true;

	expandableGroups.forEach(group => {
		// Find the trigger input (radio or checkbox)
		const triggerInput = group.querySelector('input[type="radio"], input[type="checkbox"]');

		// Only validate if trigger is checked
		if (!triggerInput || !triggerInput.checked) return;

		// Find the expandable content
		const expandableContent = group.querySelector('.expandable-content');
		if (!expandableContent) return;

		// Validate inputs inside expandable content (regardless of visibility)
		const childInputs = expandableContent.querySelectorAll('input, textarea, select');
		childInputs.forEach(input => {
			if (input.type === 'checkbox' || input.type === 'radio') return;
			if (!input.value.trim()) {
				isValid = false;
			} else if (input.type === 'number') {
				const val = parseFloat(input.value);
				const min = input.min !== '' ? parseFloat(input.min) : null;
				if (isNaN(val) || (min !== null && val < min)) {
					isValid = false;
				}
			}
		});
	});

	return isValid;
}


/**
 * Validate inputs in a step
 * @param {HTMLElement} step - Step element to validate
 * @returns {boolean} - Whether the step is valid
 */
export function validateStep(step) {
	const inputs = step.querySelectorAll('input[required], textarea[required], select[required]');
	const nextBtn = step.querySelector('.next-btn');

	if (!nextBtn) return true;

	let isValid = true;

	inputs.forEach(input => {
		if (input.type === 'radio') {
			// For radio buttons, check if any radio with the same name is checked
			const radios = step.querySelectorAll(`input[name="${input.name}"]`);
			const isChecked = Array.from(radios).some(radio => radio.checked);
			if (!isChecked) {
				isValid = false;
			}
		} else if (input.type === 'checkbox') {
			// For checkboxes, check if at least one is checked (if required)
			if (!input.checked) {
				isValid = false;
			}
		} else {
			// For text, number, textarea, etc.
			if (!input.value.trim()) {
				isValid = false;
			} else if (input.type === 'number') {
				const val = parseFloat(input.value);
				const min = input.min !== '' ? parseFloat(input.min) : null;
				const max = input.max !== '' ? parseFloat(input.max) : null;
				const step = parseFloat(input.step) || 0;

				if (isNaN(val)) {
					isValid = false;
				} else if (min !== null && val < min) {
					isValid = false;
				} else if (max !== null && val > max) {
					isValid = false;
				} else if (step === 1 && val !== Math.floor(val)) {
					isValid = false;
				}
			}
		}
	});

	// Always validate expandable inputs (even if no required inputs exist)
	const expandableValid = validateExpandableInputs(step);
	isValid = isValid && expandableValid;

	// Enable or disable the next button
	nextBtn.disabled = !isValid;

	return isValid;
}

/**
 * Setup validation listeners for all steps
 * @param {NodeList} steps - All form steps
 */
export function setupValidation(steps) {
	steps.forEach(step => {
		const inputs = step.querySelectorAll('input, textarea, select');

		inputs.forEach(input => {
			// Add input event listener for text/number inputs
			input.addEventListener('input', () => {
				validateStep(step);
			});

			// Add change event listener for radio/checkbox/select
			input.addEventListener('change', () => {
				validateStep(step);
			});
		});

		// Initial validation
		validateStep(step);
	});
}
